/** Chart utilities shared by the insights chart set. */

export const SERIES_VARS = ["--viz-s1", "--viz-s2", "--viz-s3", "--viz-s4", "--viz-s5", "--viz-s6", "--viz-s7", "--viz-s8"] as const;

/** Categorical color for slot i (0-based). Fixed order, never cycled: past 8 returns the "Other" gray. */
export function seriesColor(i: number): string {
  const v = SERIES_VARS[i];
  return v ? `var(${v})` : "var(--viz-muted)";
}

export type Rating = "good" | "needs-improvement" | "poor";
export function ratingColor(r: Rating | undefined): string {
  if (r === "good") return "var(--viz-good)";
  if (r === "needs-improvement") return "var(--viz-warning)";
  if (r === "poor") return "var(--viz-critical)";
  return "var(--viz-muted)";
}

/** Compact number: 1,284 · 12.9K · 4.2M. */
export function compact(n: number | undefined | null, digits = 1): string {
  if (n === undefined || n === null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(digits)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(digits)}M`;
  if (abs >= 1e4) return `${(n / 1e3).toFixed(digits)}K`;
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function pct(n: number | undefined, digits = 1): string {
  if (n === undefined || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

export function ms(n: number | undefined): string {
  if (n === undefined || Number.isNaN(n)) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)} s`;
  return `${Math.round(n)} ms`;
}

/** Clean axis ticks: 0 / 1,000 / 2,000 … `count` steps, never above the next nice bound. */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const rough = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / mag;
  let step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  // Occurrence counts are whole numbers. A max of 1 should not read as 0.25.
  if (Number.isInteger(max) && step < 1) step = 1;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return ticks;
}

/** Epoch ms from a point time: numbers and numeric strings are epoch ms, anything else is parsed as a date. */
export function toMs(t: string | number): number {
  if (typeof t === "number") return t;
  if (/^\d+(\.\d+)?$/.test(t)) return Number(t);
  return new Date(t).getTime();
}

export function shortTime(t: string | number, spanMs: number): string {
  const d = new Date(toMs(t));
  if (Number.isNaN(d.getTime())) return String(t);
  if (spanMs <= 36 * 3600_000) return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Rounded top on a vertical bar (4px data end, square baseline). */
export function roundedTopPath(x: number, y: number, w: number, h: number, r = 4): string {
  const rr = Math.min(r, w / 2, Math.max(0, h));
  const bottom = y + h;
  return `M${x},${bottom} V${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${bottom} Z`;
}

/** Rounded right end on a horizontal bar (4px data end, square baseline). */
export function roundedRightPath(x: number, y: number, w: number, h: number, r = 4): string {
  const rr = Math.min(r, h / 2, Math.max(0, w));
  return `M${x},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h - rr} Q${x + w},${y + h} ${x + w - rr},${y + h} H${x} Z`;
}

/** Signed delta text with direction, e.g. "+12.3%" or "−4". */
export function signed(n: number, digits = 1, suffix = ""): string {
  if (Number.isNaN(n)) return "—";
  const s = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${s}${Math.abs(n).toFixed(digits)}${suffix}`;
}
